import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { applyExcelLandscapePrintSetup } from '../../../utils/applyExcelLandscapePrintSetup';
import { getTimeTrackerExcelFonts } from '../../../utils/timeTrackerExportFont';
import { DateUtil } from '@src/packages/utils/dates';

const { defaultFont, boldFont } = getTimeTrackerExcelFonts();

const applyDefaultFont = worksheet => {
  worksheet.eachRow(row => {
    row.eachCell(cell => {
      if (!cell.font || !cell.font.bold) cell.font = defaultFont;
    });
  });
};

/**
 * Converts titles from Ant Design (which can be JSX) to a safe string
 */
const getColumnTitle = title => {
  if (title === null || title === undefined) return '';
  if (typeof title === 'string') return title;
  if (typeof title === 'number') return String(title);

  if (title?.props) {
    if (title.props.id) return title.props.id;
    if (title.props.defaultMessage) return title.props.defaultMessage;
    if (typeof title.props.children === 'string') return title.props.children;
  }
  return '';
};

/**
 * Converts any value to an "exportable" value without breaking due to circular references.
 */
const normalizeValue = value => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';

  // Dates
  if (value instanceof Date) return value.toISOString();

  // Arrays
  if (Array.isArray(value)) return value.join(', ');

  // Objects
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '[Objeto no exportable]';
    }
  }

  return String(value);
};

export const downloadActiveEnterprisesAsExcel = async (
  data,
  columns,
  fileName = 'active_enterprises',
  formatIdCard,
) => {
  try {
    let currentRow = 1;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Empresas Activas');
    const safeColumns = Array.isArray(columns) ? columns : [];
    const safeData = Array.isArray(data) ? data : [];

    workbook.creator = 'Operonda';
    workbook.created = new Date();
    worksheet.getCell(`A${currentRow}`).value = 'Reporte de Empresas Activas';
    worksheet.getCell(`A${currentRow}`).font = boldFont;
    worksheet.mergeCells(currentRow, 1, currentRow, safeColumns.length || 1);
    currentRow += 2;

    // Table headers
    safeColumns.forEach((column, colIndex) => {
      const cell = worksheet.getCell(currentRow, colIndex + 1);

      cell.value = getColumnTitle(column?.title);
      cell.font = boldFont;

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF0F0F0' },
      };

      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    currentRow++;

    // Data rows
    safeData.forEach(record => {
      safeColumns.forEach((column, colIndex) => {
        const cell = worksheet.getCell(currentRow, colIndex + 1);
        const dataIndex = column?.dataIndex;
        let value = '';

        if (dataIndex) value = normalizeValue(record?.[dataIndex]);
        if (dataIndex === 'auditUrl' && record?.auditUrl) value = normalizeValue(record.auditUrl);
        if (dataIndex === 'contractExpiryDate' && record?.contractExpiryDate) {
          value = DateUtil.toDateString(record.contractExpiryDate, DateUtil.DEFAULT_DT_DATE_FORMAT);
        }
        if (dataIndex.toLowerCase() === 'rut' && record?.RUT) {
          value = formatIdCard(record.RUT);
        }
        cell.value = value;
        cell.font = { ...defaultFont };

        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      currentRow++;
    });

    // Auto-fit columns
    worksheet.columns.forEach(column => {
      let maxLength = 10;

      column.eachCell({ includeEmpty: true }, cell => {
        let cellValue = '';

        if (cell.value !== null && cell.value !== undefined) {
          try {
            cellValue = cell.value.toString();
          } catch (e) {
            cellValue = '';
          }
        }

        const lines = cellValue.split('\n');

        lines.forEach(line => {
          if (line.length > maxLength) {
            maxLength = line.length;
          }
        });
      });

      column.width = Math.min(Math.max(maxLength + 2, 10), 50);
    });

    applyDefaultFont(worksheet);
    applyExcelLandscapePrintSetup(worksheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const date = new Date().toISOString().split('T')[0];
    const type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([buffer], { type });
    FileSaver.saveAs(blob, `${fileName}_${date}.xlsx`);
  } catch (error) {
    console.error('Error downloading Excel:', error);
    throw error;
  }
};
